/**
 * @file InactivityModal.tsx
 *
 * @description Warns about impending session timeout due to inactivity, with
 * a live countdown. Owns the countdown/auto-logout lifecycle; the dialog
 * shell is the shared ConfirmDialog in blocking mode (the previous
 * implementation was equally non-dismissable — it had no close handler).
 *
 * @module Modals
 */
import React, { useState, useEffect } from "react";
import { alpha, Box, LinearProgress, Typography } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import { ConfirmDialog } from "../index";

interface InactivityModalProps {
  open: boolean;
  onContinue: () => void;
  onLogout: () => Promise<void>;
  expiryTimestamp: number; // unix seconds
}

export const InactivityModal: React.FC<InactivityModalProps> = ({
  open,
  onContinue,
  onLogout,
  expiryTimestamp,
}) => {
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const [totalTime, setTotalTime] = useState<number>(0);

  useEffect(() => {
    if (!open) return;

    // Calculate initial remaining time and set total time
    const now = Date.now() / 1000;
    const initialRemaining = Math.max(0, Math.floor(expiryTimestamp - now));
    setRemainingTime(initialRemaining);

    // Re-seed the progress baseline on every open: each warning carries a
    // fresh expiry, and a total left over from a previous open skews the bar
    // (remaining can even exceed it).
    setTotalTime(initialRemaining);

    // Update remaining time every second
    const interval = setInterval(async () => {
      const now = Date.now() / 1000;
      const remaining = Math.max(0, Math.floor(expiryTimestamp - now));
      setRemainingTime(remaining);

      // Auto-logout when time expires. Clear before the await — onLogout
      // saves over the network, and the interval must not fire again
      // (re-invoking logout) while that's in flight.
      if (remaining <= 0) {
        clearInterval(interval);
        await onLogout();
      }
    }, 1000);

    return () => clearInterval(interval);
    // Keyed on `open` only, deliberately. Each inactivity warning is a fresh
    // open cycle that re-seeds the countdown and progress baseline from the
    // current expiryTimestamp (see above); the session is extended by closing
    // the modal ("Continue Editing"), not by mutating expiry while it's open,
    // so there's no stale-deadline case to react to. onLogout is excluded on
    // purpose too — it's a non-memoized prop, so listing it would restart the
    // interval on every parent render and the countdown would never advance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  const progressPercentage = totalTime > 0 ? (remainingTime / totalTime) * 100 : 0;

  return (
    <ConfirmDialog
      open={open}
      blocking
      onClose={onContinue}
      onConfirm={onContinue}
      onCancel={() => {
        void onLogout();
      }}
      tone="error"
      confirmColor="primary"
      icon={<WarningIcon />}
      title="Timeout Warning"
      description="Due to inactivity, you will be redirected to the preview page in:"
      confirmLabel="Continue Editing"
      cancelLabel="Save & Exit"
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          my: 2,
          p: 2,
          bgcolor: (t) => alpha(t.palette.error.main, 0.1),
          borderRadius: 1,
        }}
      >
        <Typography variant="h4" color="error" fontWeight="bold">
          {formatTime(remainingTime)}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progressPercentage}
        color="error"
        sx={{ mb: 2, height: 8, borderRadius: 4 }}
      />
      <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
        For security, your editing session will be saved and exited after a
        period of inactivity. To continue working, please click the
        &quot;Continue Editing&quot; button.
      </Typography>
    </ConfirmDialog>
  );
};
